/* THE WEEK LINE — the direction's one signature element (docs/WEB-DESIGN-DIRECTION.md §2).
 *
 * A Monday-first strip of seven cells for ONE athlete, each cell the day's state in the phone's
 * own vocabulary: a done day is a tick, today is the accent, a rest day is nothing. Pure, so
 * `weekLine.test.ts` executes it; `days.ts` owns what a day IS (UTC day keys) and this file
 * never re-derives that.
 *
 * ⚠️ A DAY IS "DONE" WHEN A SET WAS LOGGED ON IT — the roster's own rule (`roster.ts`: `set_logs`,
 * never `workout_sessions.completed_at`, which is empty for most real training). "Missed" is
 * assigned, in the past, and no set logged. "Due" is assigned and not yet past. Nothing assigned
 * is "rest" — even if the athlete trained (then it is "done": a set logged is a set logged).
 */
import { dayKeyOf, shiftDayKey } from "./days";

export type DayState = "rest" | "due" | "done" | "missed";

export type WeekCell = {
  /** `YYYY-MM-DD` */
  dayKey: string;
  /** Mon … Sun */
  label: string;
  /** 1–31 */
  dayOfMonth: number;
  state: DayState;
  isToday: boolean;
  /** What TRAINING is assigned that day, by name, for the card variant. */
  titles: string[];
  /** The day's tasks, by name — shown, never scored. See `weekCells`. */
  taskTitles: string[];
};

const LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * W106 (William, 2026-09-30: *"just have M to S"*) — the strip cell's letter, EVERY day, whatever it holds: the state is
 * said by the cell's fill and outline, never by swapping the letter for a mark. One rule, so the letter cannot come to
 * depend on the state again (it used to be drawn for a due day only).
 */
export function stripLetter(c: Pick<WeekCell, "label">): string {
  return c.label.charAt(0);
}

/** The Monday of the week holding `today`, as a day key. Monday-first, never the locale's. */
export function mondayKey(today: Date, weekOffset = 0): string {
  const dow = (today.getUTCDay() + 6) % 7; // Mon=0 … Sun=6
  return shiftDayKey(today, -dow + weekOffset * 7);
}

/**
 * The week, as marks.
 *
 * 🔴 `tasks` IS A SEPARATE MAP AND THAT IS THE WHOLE POINT (2026-09-18). A task is an assignment —
 * `coach_workouts.kind = 'task'` — so passing his DAILY WRIST SERIES in `assigned` made every rest
 * day "assigned", and a past rest day with nothing logged therefore wore the red MISSED ring. Two
 * of the five days his banner once called "not trained" were rest days he had nothing to do on.
 *
 * ⚠️ AND THE ROSTER WAS FIXED BEFORE THIS WAS, so for a while the two screens disagreed about the
 * same man's week — the defining bug class of this repo, one layer down from where it was caught.
 * The rule lives HERE now, and both callers pass the same two maps.
 *
 * ⚠️ TASKS ARE STILL SHOWN. They are listed in `taskTitles` so the day is not blank; what they may
 * not do is decide the day's STATE. A task is reported, never scored.
 */
export function weekCells(
  today: Date,
  assigned: Map<string, string[]>, // dayKey → workout names
  trained: Set<string>, // dayKeys with a logged set
  weekOffset = 0,
  tasks: Map<string, string[]> = new Map(),
): WeekCell[] {
  const monday = mondayKey(today, weekOffset);
  const todayKey = dayKeyOf(today);
  const mondayDate = new Date(monday + "T00:00:00Z");
  return LABELS.map((label, i) => {
    const dayKey = shiftDayKey(mondayDate, i);
    const titles = assigned.get(dayKey) ?? [];
    const taskTitles = tasks.get(dayKey) ?? [];
    // TRAINING decides the state. A day holding only tasks is a REST day that has something on it.
    const isAssigned = titles.length > 0;
    const isTrained = trained.has(dayKey);
    let state: DayState = "rest";
    if (isTrained) state = "done";
    else if (isAssigned) state = dayKey < todayKey ? "missed" : "due";
    return {
      dayKey,
      label,
      dayOfMonth: Number(dayKey.slice(8, 10)),
      state,
      isToday: dayKey === todayKey,
      titles,
      taskTitles,
    };
  });
}

/* ── THE FOUR DATE SPELLINGS, AND THERE ARE ONLY FOUR ───────────────────────────────────────
 *
 * 🔴 THE WEB APP HAD TWO VOCABULARIES FOR ONE FACT AND NOBODY OWNED THE SECOND. The roster, the
 * client overview and every week strip said `Wed 16 Sep` through `shortDate`; the Metrics tab,
 * the body-metrics table and the chart said `2026-09-18` — a raw `.slice(0, 10)` at seven call
 * sites, which is not a spelling anybody chose, it is the database's storage format reaching a
 * coach's screen. Everfit prints `Oct 4, 2025` wherever a date sits in a TABLE or on a CARD
 * (`EVERFIT-MAP` §2-ter: `Weight / 80 kg / Oct 4, 2025`), so that is what `longDate` is.
 *
 * ⚠️ THEY SHARE ONE MONTH TABLE AND ONE PARSE, IN ONE FILE, so the three cannot drift into four.
 * Every one of them reads UTC: `new Date("2026-10-01")` is midnight UTC, and a machine west of
 * Greenwich renders the day before if you ask it in local time.
 *
 * ⚠️ AND AN UNPARSEABLE KEY COMES BACK UNCHANGED rather than as `Invalid Date` — the input is a
 * database day key, so the honest failure is to show what was stored.
 */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * 🔴 W30(g) — A DAY KEY IS REAL ONLY IF IT COMES BACK AS ITSELF. `Number.isNaN` IS NOT ENOUGH.
 *
 * **MEASURED inside W30(a), by a ruler that went red on its first run:**
 * `new Date("2026-02-30T00:00:00Z")` is a perfectly valid `Date` — it is **2 March** — so a shape
 * test and a NaN test both pass while every formatter below answers about a DIFFERENT DAY from
 * the one it was handed, silently. `taskDay.ts` guarded itself with this round trip the same day
 * and filed the shared formatters as W30(g) rather than widening them mid-change; this is that
 * change.
 *
 * ⚠️ **IT IS THE SAME RULE W22(d) ALREADY USES for the calendar's own `?date=`** — *a day is
 * validated by the CALENDAR, not only by shape* — so this is one rule in one place rather than a
 * second one. `taskDay.ts` imports it now; it does not keep a copy. Two definitions of "is this a
 * real day" is the defining bug class with a calendar in it.
 *
 * ⚠️ **AND THE FAILURE STAYS THE ONE THESE FORMATTERS ALREADY CHOSE: the key comes back
 * UNCHANGED**, never `Invalid Date` and never a guess. The input is a database day key, so
 * showing what was stored is the honest answer — a rendered `Mar 2, 2026` for a stored
 * `2026-02-30` is a fact about nothing.
 */
export function realDay(dayKey: string): Date | null {
  /* ⚠️ **THE SHAPE TEST THIS CARRIED IS GONE, AND IT WENT BECAUSE A MUTANT SURVIVED.** Removing
     `/^\d{4}-\d{2}-\d{2}$/` changed NO clause, so the batch reported it as dead weight rather
     than as a missing ruler — and it is: `toISOString()` can only ever emit `YYYY-MM-DD`, so any
     key that survives the comparison below already HAS that shape. MEASURED over twelve inputs
     (unpadded, expanded-year, a full timestamp, a trailing space, `20260919`, year 1, year
     275760): the two versions agree on every one. The sweep is a ruler now, so the claim is
     evidence rather than an argument. */
  const d = new Date(dayKey + "T00:00:00Z");
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10) === dayKey ? d : null;
}

/** `Mon 21 Sep` — WEEK STRIPS AND DAY CELLS ONLY, where the WEEKDAY is the thing being read. */
export function shortDate(dayKey: string): string {
  const d = realDay(dayKey);
  if (d === null) return dayKey;
  const day = LABELS[(d.getUTCDay() + 6) % 7];
  return `${day} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

/** `Fri 18 Sep 2026` — a date in a TABLE or on a CARD, where the year can differ from this one.
 *  🔴 W72 STEP 7 (Proposal 7 rule 7: "dates read `Mon 28 Sep`"): OUR grammar — weekday, day, month — with the year kept
 *  because a card or a table can hold last year's reading. It WAS Everfit's `Sep 18, 2026` (EVERFIT-MAP §2-ter), which
 *  rule 7 retires; `shortDate` is this form without the year. */
export function longDate(dayKey: string): string {
  const d = realDay(dayKey);
  if (d === null) return dayKey;
  return `${shortDate(dayKey)} ${d.getUTCFullYear()}`;
}

/** `Aug 25` — A CHART AXIS, where the year is the same at both ends and there is no room for it.
 *  ⚠️ It is NOT the compact `08-25` it replaces: that is ISO with its year cut off, and a coach
 *  reading `08-25` in a product that writes `Sep 18, 2026` two inches away has to decide which
 *  half is the month. Everfit's own axis has never been read — their charts were still drawing
 *  spinners when §2-ter was written — so this is OURS until somebody sees theirs, said out loud
 *  rather than presented as a copy. */
export function axisDate(dayKey: string): string {
  const d = realDay(dayKey);
  if (d === null) return dayKey;
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

/**
 * `Wednesday, 12 Mar 2025` — A DAY DIVIDER IN A MESSAGE THREAD, and it is THEIRS verbatim.
 *
 * 🔴 **THIS IS A FIFTH SPELLING THAT ALREADY EXISTED AND WAS MISSED BY THE PASS THAT CLAIMED
 * "THERE ARE ONLY FOUR" (2026-09-19 ~17:30).** `Inbox.tsx` carried its own `longDay` — a module
 * function at the bottom of a screen file, producing `Sunday, 30 August` through
 * `toLocaleDateString("en-GB", …)`. The morning's date-vocabulary commit hunted `.slice(0, 10)`
 * call sites and found seven; this one is a different SHAPE (a private formatter, not a raw
 * key), so the grep that closed that question could never have seen it, and the header two
 * blocks above went on asserting a closed vocabulary that was open.
 *
 * ⚠️ **AND IT WAS NOT MERELY A FIFTH NAME — IT WAS A FIFTH *RULE*.** `toLocaleDateString` reads
 * the BROWSER's locale, so the same thread said `Sunday, 30 August` on one coach's machine and
 * `dimanche 30 août` on another's, two inches from a `Sep 18, 2026` that is hard-coded English.
 * It also hard-coded `en-GB` as a fallback the rest of this file does not use. One month table,
 * one UTC parse, one answer.
 *
 * ⚠️ **THE FORM IS EVERFIT'S OWN** (`EVERFIT-MAP` §10-ter depth 1: `Wednesday, 12 Mar 2025`),
 * which is why it carries the YEAR where our `shortDate` does not: a thread is scrolled through
 * years, and `Wednesday, 12 Mar` in a conversation going back to 2025 is a date a coach has to
 * guess the year of.
 */
export function dayDivider(dayKey: string): string {
  const d = realDay(dayKey);
  if (d === null) return dayKey;
  return `${WEEKDAYS[d.getUTCDay()]}, ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** `Monday 7 September` — THE PROGRESS CHART'S HEADLINE CAPTION for a selected session (W72 step 4): the phone's
 *  `.dateTime.weekday(.wide).day().month(.wide)`, the one place the whole month is spelled, because the caption sits
 *  alone under a big number with room for it. Same UTC parse, same tables, never the browser's locale. */
export function weekdayDate(dayKey: string): string {
  const d = realDay(dayKey);
  if (d === null) return dayKey;
  return `${WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()} ${FULL_MONTHS[d.getUTCMonth()]}`;
}

const FULL_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September",
  "October", "November", "December"];

/** The seven full weekday names, SUNDAY-FIRST because that is what `getUTCDay()` returns.
 *  ⚠️ `LABELS` above is MONDAY-first (it heads a week strip), so the two cannot share an array —
 *  and indexing this one with `(getUTCDay() + 6) % 7` would silently name every day wrong. */
export const WEEKDAYS = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];

/**
 * `Sep 14 – 20` — ONE WEEK'S RANGE ON A PHONE, and it is the APP's own formatter rather than a
 * fourth invention (`ClientDetailView.weekRange`, read in the Swift source).
 *
 * 🔴 THE MONTH GOES ON THE FIRST DATE AND IS DROPPED FROM THE SECOND WHEN THE WEEK DOES NOT
 * CROSS ONE. The app's own comment says why it is that way round and not the other: putting it
 * on the second instead reads `3 – Aug 9`, which looks like a typo rather than a range.
 *
 * ⚠️ IT IS NOT `rangeLabel`. That one spells both ends in full with their weekdays
 * (`Mon 14 Sep – Sun 27 Sep`) because the desktop draws up to FOUR weeks and the reader needs to
 * know which day each end is. A phone draws one week, Monday to Sunday by construction, so the
 * weekdays are the one thing the label does not have to say.
 */
export function appWeekRange(mondayDayKey: string): string {
  /* W30(g) — the same round trip as its four neighbours. Its caller hands it a key derived from a
     `Date`, so this cannot fire today; that was true of `longDate` too, right up until a form was
     built whose whole job was to say which day it wrote to. */
  const from = realDay(mondayDayKey);
  if (from === null) return mondayDayKey;
  /* 🔴 W30(k) — IT IS `compactRange` OF ITS OWN WEEK NOW, NOT A SECOND COPY OF THAT GRAMMAR. The
     Tasks bar needed the same short form for a span of up to FOUR weeks, and the honest way to
     give a second caller a spelling is to widen the rule rather than to write it out again —
     `realDay` one function up is this file's own lesson, from the day `taskDay` kept a copy. */
  return compactRange(mondayDayKey, dayKeyAt(from.getTime() + 6 * 86_400_000));
}

/**
 * `Sep 14 – 20`, `Sep 14 – 27`, `Sep 14 – Oct 11` — A RANGE WITH NO ROOM FOR WEEKDAYS.
 *
 * 🔴 W30(k) — THE TASKS CALENDAR'S RANGE WRAPPED ONTO TWO CENTRED LINES AT 375 (filed by W30(i)
 * after its stills; `Mon 14 Sep – Sun 27 Sep` does not fit the navigator's centre column once the
 * two 2.75rem tap targets are placed). The Training bar escapes it by printing `appWeekRange` —
 * but that one takes a MONDAY and spells its own week, and the Tasks bar's span reaches four
 * weeks, so calling it there would have printed a range that is simply false.
 *
 * ⚠️ **SO THIS IS THE SAME GRAMMAR, NOT A SIXTH SPELLING, AND THAT IS THE WHOLE DECISION.**
 * `weekLine.ts` already records that this product carries five date spellings and that the fifth
 * arrived unnoticed; the fix for a label that is too long is not a new vocabulary, it is the one
 * the bar beside it already speaks. `appWeekRange` is now this function applied to one week, so
 * the two bars cannot drift: there is one rule for *a range with the month said once*.
 *
 * ⚠️ **THE YEAR IS COMPARED AS WELL AS THE MONTH**, which `appWeekRange` did not do and did not
 * need to: a week cannot cross a year without crossing a month, but a four-week span read a year
 * apart can — and `Sep 14 – 20` for `2026-09-14 → 2027-09-20` would be a range that names the
 * wrong dates rather than merely an ambiguous one.
 *
 * ⚠️ **AND A KEY THAT IS NOT A DAY COMES BACK UNCHANGED, AT ITS OWN END** — the failure the five
 * formatters above already chose (W30(g)). The input is a database day key, so showing what was
 * stored is the honest answer, and showing it for the end that is broken says which end it was.
 */
export function compactRange(fromKey: string, toKey: string): string {
  const from = realDay(fromKey);
  const to = realDay(toKey);
  if (from === null || to === null) return `${fromKey} – ${toKey}`;
  const head = `${MONTHS[from.getUTCMonth()]} ${from.getUTCDate()}`;
  const sameMonth = from.getUTCMonth() === to.getUTCMonth()
    && from.getUTCFullYear() === to.getUTCFullYear();
  const tail = sameMonth
    ? String(to.getUTCDate())
    : `${MONTHS[to.getUTCMonth()]} ${to.getUTCDate()}`;
  return `${head} – ${tail}`;
}

/** The day key inside an instant — every store here holds UTC timestamps. */
export function dayKeyAt(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/* `Sat 12 – Fri 18 Sep`, and both months where the window crosses one. The month is said ONCE
   when it is the same on both ends, for the reason the assign panel says it once: repeating it
   carries no information and makes the line longer than the fact inside it. */
export function shortRange(fromKey: string, toKey: string): string {
  const a = shortDate(fromKey);
  const b = shortDate(toKey);
  if (fromKey.slice(0, 7) === toKey.slice(0, 7)) {
    return `${a.split(" ").slice(0, 2).join(" ")} \u2013 ${b}`;
  }
  return `${a} \u2013 ${b}`;
}

export function describeCell(c: WeekCell): string {
  /* ⚠️ "nothing assigned" IS ONLY TRUE WHEN THERE ARE NO TASKS EITHER. Taking tasks out of the
     STATE (so a rest day stopped wearing a red MISSED ring) made this sentence say the day was
     empty while his wrist series sat on it — the fix moving the error rather than removing it.
     A task is reported, never scored: it belongs in the sentence and not in the state. */
  const what = c.titles.length > 0
    ? c.titles.join(", ")
    : c.taskTitles.length > 0
      ? `${c.taskTitles.join(", ")} (task${c.taskTitles.length === 1 ? "" : "s"})`
      : "nothing assigned";
  const state =
    c.state === "done" ? "trained" : c.state === "missed" ? "not trained" : c.state === "due" ? "due" : "rest";
  return `${shortDate(c.dayKey)}${c.isToday ? " (today)" : ""}: ${state} — ${what}`;
}
